import type { AgentOptions } from '@newrelic/browser-agent/loaders/agent';

import {
  CHECKOUT_NEW_RELIC_LICENSE_KEY,
  CHECKOUT_NEW_RELIC_ACCOUNT_ID,
  CHECKOUT_NEW_RELIC_APPLICATION_ID,
  CHECKOUT_NEW_RELIC_TRUST_KEY,
} from '@chk/env-variables';
import type { GlobalApiErrorCode } from '@data/global-api-errors';
import { GLOBAL_API_ERROR_CODES } from '@data/global-api-errors';
import type { ServiceType } from '@utility/checkout-utils';
import { COMMON_HEADERS_KEYS, SERVICE_TYPE } from '@utility/checkout-utils';

import { TRANSLATION_KEYS } from './utils/TranslationKeys';

export const SCA_ERRORS = {
  IBAN: 'IBAN',
  CREDENTIALS: 'CREDENTIALS',
  UNKNOWN: 'UNKNOWN',
} as const;

export const REASONS_KEYS = {
  WANT_TO_PAY_ANOTHER_WAY: 'WANT_TO_PAY_ANOTHER_WAY',
  DID_NOT_RECEIVE_CODE: 'DID_NOT_RECEIVE_CODE',
  CODE_IS_NOT_WORKING: 'CODE_IS_NOT_WORKING',
  DO_NOT_HAVE_DEVICE_AT_HAND: 'DO_NOT_HAVE_DEVICE_AT_HAND',
  DO_NOT_REMEMBER_MY_BANK_LOGIN_CREDENTIALS:
    'DO_NOT_REMEMBER_MY_BANK_LOGIN_CREDENTIALS',
  MY_AUTHORISATION_METHOD_IS_NOT_DISPLAYED:
    'MY_AUTHORISATION_METHOD_IS_NOT_DISPLAYED',
  DID_NOT_RECEIVE_ANY_NOTIFICATION: 'DID_NOT_RECEIVE_ANY_NOTIFICATION',
  MY_ACCOUNT_IS_NOT_DISPLAYED: 'MY_ACCOUNT_IS_NOT_DISPLAYED',
  OTHER: 'OTHER',
} as const;

export const CANCELLATION_REASONS = {
  wantToPayAnotherWay: {
    label: TRANSLATION_KEYS.I_WANT_TO_PAY_ANOTHER_WAY,
    value: REASONS_KEYS['WANT_TO_PAY_ANOTHER_WAY'],
  },
  didNotReceiveTheCode: {
    label: TRANSLATION_KEYS.I_DID_NOT_RECEIVE_CODE,
    value: REASONS_KEYS['DID_NOT_RECEIVE_CODE'],
  },
  codeIsNotWorking: {
    label: TRANSLATION_KEYS.CODE_IS_NOT_WORKING,
    value: REASONS_KEYS['CODE_IS_NOT_WORKING'],
  },
  doNotHaveMyDeviceAtHand: {
    label: TRANSLATION_KEYS.I_DO_NOT_HAVE_MY_DEVICE,
    value: REASONS_KEYS['DO_NOT_HAVE_DEVICE_AT_HAND'],
  },
  doNotRememberMyBankLoginCredentials: {
    label: TRANSLATION_KEYS.I_DO_NOT_REMEMBER_MY_BANK_CREDENTIALS,
    value: REASONS_KEYS['DO_NOT_REMEMBER_MY_BANK_LOGIN_CREDENTIALS'],
  },
  myAuthorisationMethodIsNotDisplayed: {
    label: TRANSLATION_KEYS.MY_AUTHENTICATION_METHOD_IS_NOT_DISPLAYED,
    value: REASONS_KEYS['MY_AUTHORISATION_METHOD_IS_NOT_DISPLAYED'],
  },
  myAccountIsNotDisplayed: {
    label: TRANSLATION_KEYS.MY_BANK_ACCOUNT_IS_NOT_DISPLAYED,
    value: REASONS_KEYS['MY_ACCOUNT_IS_NOT_DISPLAYED'],
  },
  other: {
    label: TRANSLATION_KEYS.OTHER,
    value: REASONS_KEYS['OTHER'],
  },
} as const;

const X_VOLT_API_VERSION = '1';

export const AIS_PATH = 'verifications';

export const SCA_API_ENDPOINTS = {
  getPaymentDetails: (
    paymentId: string,
    path: ServiceType = SERVICE_TYPE.PIS
  ) => `/${path}/${paymentId}`,
  patchPaymentDetails: (
    paymentId: string,
    path: ServiceType = SERVICE_TYPE.PIS
  ) => `/${path}/${paymentId}`,
  postCancelPayment: (
    paymentId: string,
    path: ServiceType = SERVICE_TYPE.PIS
  ) => `/${path}/${paymentId}/cancel`,
} as const;

export const getScaHeaders = ({ authToken }: { authToken: string | null }) => ({
  [COMMON_HEADERS_KEYS.X_VOLT_API_VERSION]: X_VOLT_API_VERSION,
  [COMMON_HEADERS_KEYS.X_VOLT_APP_AUTHORIZATION]: `Bearer ${authToken}`,
  [COMMON_HEADERS_KEYS.CONTENT_TYPE]: 'application/json',
  [COMMON_HEADERS_KEYS.X_VOLT_APP]: 'scaApp',
});

export const ERROR_CODES_IN_TOAST: GlobalApiErrorCode[] = [
  GLOBAL_API_ERROR_CODES.CODE_403.ACCESS_DENIED,
  GLOBAL_API_ERROR_CODES.CODE_404.RESOURCE_NOT_FOUND,
  GLOBAL_API_ERROR_CODES.CODE_409.CONFLICT,
  GLOBAL_API_ERROR_CODES.CODE_409.RESOURCE_NOT_MODIFIABLE,
];

export const ERROR_CODES_IN_ALERT: (GlobalApiErrorCode | string)[] = [
  GLOBAL_API_ERROR_CODES.CODE_500.INTERNAL_SERVER_ERROR,
  GLOBAL_API_ERROR_CODES.CODE_424.RETRY_LIMIT_EXCEEDED,
  'TypeError',
];

export const FLOW_TYPE = {
  AIS: 'AIS',
  PIS: 'PIS',
} as const;

const NEW_RELIC_URL = 'bam.eu01.nr-data.net';

export const NEW_RELIC_CONFIG = {
  info: {
    beacon: NEW_RELIC_URL,
    errorBeacon: NEW_RELIC_URL,
    licenseKey: CHECKOUT_NEW_RELIC_LICENSE_KEY,
    applicationID: CHECKOUT_NEW_RELIC_APPLICATION_ID,
    sa: 1,
  },
  init: {
    distributed_tracing: { enabled: true },
    privacy: { cookies_enabled: true },
    ajax: { deny_list: [NEW_RELIC_URL] },
  },
  loader_config: {
    accountID: CHECKOUT_NEW_RELIC_ACCOUNT_ID,
    trustKey: CHECKOUT_NEW_RELIC_TRUST_KEY,
    agentID: CHECKOUT_NEW_RELIC_APPLICATION_ID,
    licenseKey: CHECKOUT_NEW_RELIC_LICENSE_KEY,
    applicationID: CHECKOUT_NEW_RELIC_APPLICATION_ID,
  },
} as const satisfies AgentOptions;
